import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TeamAvatar } from './pools/poolFx';
import BetLike from './BetLike';
import { demoName } from '../social';
import './chat.css';

const GROUP_MINS = 15;      // bets from one person this close together become one post
const BIG_BET = 500;        // gets the BIG BET mark
const ROWS_SHOWN = 3;       // a post with more bets opens on demand
const SMALL_WIN = 200;      // under this profit, a win is one line
const BIG_WIN = 1000;       // from this profit, a win is the big size
const RESULT_ROWS = 3;      // a finished post this small says its record and net in one sentence
const POSTS_MAX = 60;
const TOP_ZONE = 120;       // scrolled further than this, new posts wait behind the pill
const GAP_MS = 1500;        // quiet time between two moments
const LANDS_MAX = 4;        // more new posts than this at once just appear
const LOOP_BUDGET = 3;      // at most this many flames and live dots loop at once
const FRESH_LIKE_MS = 20000;
const LAST_VISIT = 'bdb_feed_last_visit';
const DAY = 86400000;
const PREDICT_LOGOS = ['VGK', 'FLA', 'WSH', 'BOS'];   // same row of logos as the Predict the Night banner
const FLAME = 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z';
const SPARKS = [0, 1, 2, 3, 4, 5, 6, 7];
const EMBERS = [-12, -5, 3, 9, 14];
const DIGITS = '0123456789'.split('');
const REDUCED = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const JELLY = [{ transform: 'none' }, { transform: 'scale(1.02, 0.97)', offset: 0.38 }, { transform: 'scale(0.995, 1.012)', offset: 0.62 }, { transform: 'none' }];
const ROW_BUMP = [{ transform: 'none' }, { transform: 'scale(1.02)', offset: 0.45 }, { transform: 'none' }];
const NO_MOMENT = { land: new Set(), flip: new Set(), rowNew: new Set() };

const plain = (n) => String(n);
const stakeOf = (bet) => +bet.stake || 0;
const statusOf = (bet) => {
  if (bet.status !== 'settled') return 'open';
  return bet.result === 'won' || bet.result === 'lost' ? bet.result : 'void';
};
const profitOf = (bet) => Math.max(0, (+bet.payout > 0 ? +bet.payout : stakeOf(bet) * (+bet.odds || 1)) - stakeOf(bet));
const netOf = (rows) => rows.reduce((sum, r) => sum + (r.status === 'won' ? profitOf(r.bet) : r.status === 'lost' ? -stakeOf(r.bet) : 0), 0);
const time = (at) => new Date(at).getTime();

function timeAgo(at, fr) {
  const mins = Math.max(0, Math.floor((Date.now() - time(at)) / 60000));
  if (mins < 1) return fr ? 'à l’instant' : 'just now';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} ${fr ? 'j' : 'd'}`;
}
// the feed is separated by day
const dayOf = (p) => new Date(time(p.at || p.lines?.[0]?.at)).toDateString();
function dayLabel(day, fr) {
  const d = new Date(day);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === new Date().toDateString()) return fr ? 'Aujourd’hui' : 'Today';
  if (day === yesterday.toDateString()) return fr ? 'Hier' : 'Yesterday';
  return d.toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
}
function agoText(at, fr) {
  const t = timeAgo(at, fr);
  if (Date.now() - time(at) < 60000) return t;
  return fr ? `il y a ${t}` : `${t} ago`;
}

// a settled single bet turns into its result, under the same id, so its flames and its place in the diff follow it
function resultPost(row) {
  const { bet, status, likeKey } = row;
  const base = { id: `group:${likeKey}`, user_id: row.user_id, at: row.settledAt || row.at, likeKey, row };
  const profit = profitOf(bet);
  if (status === 'lost') return { ...base, kind: 'line', tone: 'lost', amount: stakeOf(bet) };
  if (bet.bet_type === 'parlay') return { ...base, kind: 'parlaywin', amount: profit };
  if (profit < SMALL_WIN) return { ...base, kind: 'line', tone: 'won', amount: profit };
  return { ...base, kind: 'win', big: profit >= BIG_WIN, amount: profit };
}

// everything the page shows comes from one snapshot of the items: a bet has one place, whatever state it's in
function buildPosts(items, betsCap) {
  const wins = new Map(items.filter((i) => i.kind === 'won').map((i) => [i.likeKey, i]));
  const rows = new Map();
  items.filter((i) => i.kind === 'placed').forEach((i) => {
    const win = wins.get(i.likeKey);
    const bet = win ? { ...i.bet, ...win.bet } : i.bet;
    rows.set(i.likeKey, { ...i, bet, status: win ? 'won' : statusOf(bet), settledAt: bet.settled_at || win?.at });
  });
  const ordered = [...rows.values()].sort((a, b) => time(a.at) - time(b.at));
  const oldest = ordered.length ? time(ordered[0].at) : 0;
  const capped = ordered.length >= betsCap;

  const current = {};
  const groups = [];
  ordered.forEach((row) => {
    const last = current[row.user_id];
    if (last && time(row.at) - last.lastAt < GROUP_MINS * 60000) { last.rows.unshift(row); last.lastAt = time(row.at); return; }
    const group = { firstKey: row.likeKey, user_id: row.user_id, rows: [row], firstAt: time(row.at), lastAt: time(row.at) };
    current[row.user_id] = group;
    groups.push(group);
  });

  const posts = [];
  groups.forEach((g) => {
    const single = g.rows.length === 1;
    if (single && ['won', 'lost'].includes(g.rows[0].status)) { posts.push(resultPost(g.rows[0])); return; }
    // the oldest posts can have bets past what's loaded: those never claim a final record
    const whole = !capped || g.firstAt - oldest >= GROUP_MINS * 60000;
    const closed = !single && whole && g.rows.every((r) => r.status !== 'open');
    const closedAt = closed ? new Date(Math.max(...g.rows.map((r) => time(r.settledAt || r.at)))).toISOString() : null;
    posts.push({ id: `group:${g.firstKey}`, kind: 'hand', user_id: g.user_id, rows: g.rows, placedAt: g.rows[0].at, at: closedAt || g.rows[0].at, closed, results: closed && g.rows.length <= RESULT_ROWS });
  });
  wins.forEach((win) => {
    if (!rows.has(win.likeKey)) posts.push(resultPost({ ...win, status: 'won', settledAt: win.at }));
  });
  const jackpots = {};
  items.forEach((i) => {
    if (i.kind === 'predict' || i.kind === 'spin') posts.push({ id: `${i.kind}:${i.likeKey}`, kind: i.kind, user_id: i.user_id, at: i.at, amount: i.amount, likeKey: i.likeKey });
    if (i.kind !== 'jackpot') return;
    if (!jackpots[i.month]) { jackpots[i.month] = { id: `jackpot:${i.month}`, kind: 'jackpot', month: i.month, at: i.at, winners: [] }; posts.push(jackpots[i.month]); }
    jackpots[i.month].winners.push(i);
  });
  return posts.sort((a, b) => time(b.at) - time(a.at)).slice(0, POSTS_MAX);
}
const shapeOf = (p) => `${p.kind}:${p.closed ? 1 : 0}:${p.rows ? p.rows.length : 0}`;
const keysOf = (p) => {
  if (p.lines) return p.lines.map((l) => l.likeKey);
  if (p.rows) return p.rows.map((r) => r.likeKey);
  return p.winners ? p.winners.map((w) => w.likeKey) : [p.likeKey];
};
const byNewest = (a, b) => time(b.at) - time(a.at);

// the page scrolls on the window or on the main panel, depending on the screen
const scrollTop = () => Math.max(window.scrollY || 0, document.querySelector('.main-content')?.scrollTop || 0);
function goTop() {
  const behavior = REDUCED ? 'auto' : 'smooth';
  window.scrollTo({ top: 0, behavior });
  document.querySelector('.main-content')?.scrollTo({ top: 0, behavior });
}
function onScreen(selector) {
  const el = document.querySelector(selector);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.bottom > 40 && r.top < window.innerHeight - 40;
}
const animateEl = (el, frames, ms) => { if (el && !REDUCED) el.animate(frames, { duration: ms, easing: 'cubic-bezier(0.37, 0, 0.63, 1)' }); };

// which side of the game the pick is on
function pickedSide(game, text) {
  if (!game || !text) return null;
  if (game.home?.name && text.includes(game.home.name)) return 'home';
  if (game.away?.name && text.includes(game.away.name)) return 'away';
  return null;
}

// bets don't save whether they were boosted, so it comes from when they were placed: a Boost Weekend
// (Friday to Sunday on the Montréal calendar, every 14 days, same as the server). Parlays and moneylines are never boosted.
const BOOST_FIRST = Date.UTC(2026, 9, 16);
function wasBoosted(bet) {
  if (!bet?.created_at || bet.bet_type === 'parlay' || bet.bet_type === 'winner') return false;
  const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' }).format(new Date(bet.created_at)).split('-').map(Number);
  const days = Math.round((Date.UTC(y, m - 1, d) - BOOST_FIRST) / DAY);
  return days >= 0 && days % 14 < 3;
}

// every number on the page: columns of digits that slide, so nothing re-renders per frame
function Roll({ text: next, from, delay = 0, slow }) {
  const [text, setText] = useState(from ? next.replace(/\d/g, '0') : next);
  useEffect(() => {
    const t = setTimeout(() => setText(next), delay || 20);
    return () => clearTimeout(t);
  }, [next, delay]);
  const chars = text.split('');
  return (
    <em className={`fn-roll${slow ? ' slow' : ''}`} aria-label={next}>
      {chars.map((c, i) => {
        const right = chars.length - 1 - i;
        if (!/\d/.test(c)) return <i key={`c${right}`} className="fn-ch">{c}</i>;
        return <i key={`d${right}`} className="fn-col" style={{ transform: `translateY(${-(+c) * 10}%)`, transitionDelay: `${right * (slow ? 120 : 50)}ms` }}>{DIGITS.map((d) => <i key={d}>{d}</i>)}</i>;
      })}
    </em>
  );
}

function Burst() {
  return <span className="bet-like-icon fn-burst" aria-hidden="true">{SPARKS.map((i) => <i key={i} className="bet-like-spark" style={{ '--a': `${i * 45}deg`, '--d': `${i % 2 ? 26 : 38}px` }} />)}</span>;
}

// one amount; when it lands it rolls, can punch once and fires one burst of the flame's sparks
function Amount({ text, size, tone, land, spark, slow, delay = 0 }) {
  const [hit, setHit] = useState(false);
  useEffect(() => {
    if (!land || !spark || REDUCED) return undefined;
    const t = setTimeout(() => setHit(true), delay + (slow ? 1300 : 800));
    return () => clearTimeout(t);
  }, [land, spark, slow, delay]);
  return (
    <div className={`fn-amount s${size} ${tone}${hit ? ' punch' : ''}`}>
      <Roll text={text} from={land} delay={land ? delay + 120 : 0} slow={slow} />
      {hit && <Burst />}
    </div>
  );
}

function Face({ id, name, size, ctx }) {
  const person = ctx.peopleById[id];
  return <TeamAvatar team={person ? ctx.avatarOf(person) : null} name={ctx.nameOf(id, name)} size={size} />;
}

// a friend's flame lands: their face pops by the flame, sparks fly, the face shrinks into it
function Arrival({ who, ctx }) {
  if (!who) return null;
  return (
    <span className="fn-arrive" aria-hidden="true">
      <span className="fn-arrive-flame"><svg viewBox="0 0 24 24" width="20" height="20"><path d={FLAME} /></svg></span>
      <span className="bet-like-icon">
        <u className="bet-like-ring" />
        {SPARKS.map((i) => <i key={i} className="bet-like-spark" style={{ '--a': `${i * 45}deg`, '--d': `${i % 2 ? 20 : 30}px` }} />)}
        {EMBERS.map((x, i) => <i key={x} className="bet-like-ember" style={{ '--x': `${x}px`, animationDelay: `${i * 50}ms` }} />)}
      </span>
      <span className="fn-arrive-face"><Face id={who.user_id} size={22} ctx={ctx} /></span>
    </span>
  );
}

function likeProps(key, ctx) {
  const list = ctx.likes[key] || [];
  return { liked: list.some((l) => l.user_id === ctx.me), count: list.length, onToggle: () => ctx.onLike(key) };
}

function TeamMark({ team, size = 30 }) {
  const [broken, setBroken] = useState(false);
  if (!team?.logo || broken) return <span className="feed-mono" style={{ width: size, height: size }}>{(team?.abbrev || team?.name || '?').slice(0, 3)}</span>;
  return <img className={`feed-logo${team.face ? ' feed-face' : ''}`} src={team.logo} alt="" style={{ width: size, height: size }} onError={() => setBroken(true)} />;
}

// the big faded logo behind a post: the team, or the game it came from
function Watermark({ team, kind }) {
  if (team?.logo) return <span className="feed-watermark team" aria-hidden="true"><img src={team.logo} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /></span>;
  if (kind === 'predict') return <span className="feed-watermark team predict" aria-hidden="true">{PREDICT_LOGOS.map((a) => <i key={a}><img src={`https://assets.nhle.com/logos/nhl/svg/${a}_light.svg`} alt="" /></i>)}</span>;
  if (kind === 'spin') return <span className="feed-watermark fn-wm-spin" aria-hidden="true"><img src="/slot/stanley-cup.png" alt="" /></span>;
  if (kind === 'parlaywin') return <span className="feed-watermark" aria-hidden="true"><img src="/brand/bdb-logo.svg" alt="" /></span>;
  if (kind === 'jackpot') return <span className="feed-watermark fn-wm-cup" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 3h10v2h4v3a4 4 0 0 1-4.2 4A5 5 0 0 1 13 14.9V18h3v3H8v-3h3v-3.1A5 5 0 0 1 7.2 12 4 4 0 0 1 3 8V5h4zm0 4H5v1a2 2 0 0 0 2 2zm10 0v3a2 2 0 0 0 2-2V7z" /></svg></span>;
  return null;
}

function Top({ post, ctx, timeText, children }) {
  return (
    <div className="feed-top">
      <Face id={post.user_id} size={40} ctx={ctx} />
      <div className="feed-top-text">
        <p><b>{ctx.nameOf(post.user_id)}</b> {children}</p>
        <time>{timeText || timeAgo(post.at, ctx.fr)}</time>
      </div>
    </div>
  );
}

function WhoLit({ keys, ctx }) {
  const ids = [...new Set(keys.flatMap((k) => (ctx.likes[k] || []).map((l) => l.user_id)))];
  if (ids.length < 2) return null;
  const you = ctx.fr ? 'toi' : 'you';
  const shown = ids.slice(0, 2).map((id) => (id === ctx.me ? you : ctx.nameOf(id))).join(', ');
  return (
    <div className="fn-lit">
      <span className="fn-lit-faces">{ids.slice(0, 3).map((id) => <Face key={id} id={id} size={18} ctx={ctx} />)}</span>
      <span>{shown}{ids.length > 2 ? ` + ${ids.length - 2}` : ''}</span>
    </div>
  );
}

function RowMoney({ row, ctx }) {
  const { bet, status } = row;
  const flip = ctx.moment.flip.has(row.likeKey);
  const odds = `@ ${(+bet.odds).toFixed(2)}`;
  if (status === 'won') return <div className={`feed-row-money${flip ? ' fn-flip' : ''}`} key="won"><b className="fn-won"><Roll text={ctx.signed(profitOf(bet))} from={flip} delay={flip ? 150 : 0} /></b><span>{odds}</span></div>;
  if (status === 'lost') return <div className="feed-row-money" key="lost"><b className="fn-struck">{ctx.money(stakeOf(bet))}<u className={flip ? 'draw' : undefined} /></b><span>{ctx.fr ? 'perdu' : 'lost'}</span></div>;
  if (status === 'void') return <div className="feed-row-money" key="void"><b className="fn-void">{ctx.money(stakeOf(bet))}</b><span>{ctx.fr ? 'remboursé' : 'refunded'}</span></div>;
  return <div className="feed-row-money" key="open"><b>{ctx.money(stakeOf(bet))}</b><span>{wasBoosted(bet) && <img className="fn-patch" src="/brand/bdb-patch-icon.svg" alt="Boost" />}{odds}</span></div>;
}

function LiveLine({ live }) {
  return <span className="fn-live"><i className="fn-dot" />{live.period && <>{live.period} ·&nbsp;</>}{live.away}&nbsp;<Roll text={plain(live.as)} />&nbsp;-&nbsp;<Roll text={plain(live.hs)} />&nbsp;{live.home}</span>;
}

function BetRow({ row, ctx, n, enter }) {
  const { bet, status, likeKey } = row;
  const parlay = bet.bet_type === 'parlay';
  const game = parlay ? null : ctx.gameInfo(bet);
  const pick = parlay ? ctx.parlayName(row.legs) : ctx.betText(bet);
  const side = pickedSide(game, pick);
  const live = status === 'open' && !parlay ? ctx.liveOf?.(bet) : null;
  return (
    <BetLike className={`feed-row fn-press${ctx.moment.rowNew.has(likeKey) ? ' fn-row-new' : ''}${enter ? ' fn-row-in' : ''}`} style={{ '--i': n }} {...likeProps(likeKey, ctx)}>
      {parlay ? <span className="feed-mono">{row.legs}×</span> : <TeamMark team={game ? game[side || 'home'] : null} />}
      <div className="feed-row-main" data-like={likeKey}>
        <b>{pick}</b>
        {live ? <LiveLine live={live} /> : <span>{parlay ? (ctx.fr ? 'Sélections cachées' : 'Picks hidden') : bet.game_description}</span>}
      </div>
      <RowMoney row={row} ctx={ctx} />
      <Arrival key={ctx.arrive[likeKey]?.n} who={ctx.arrive[likeKey]} ctx={ctx} />
    </BetLike>
  );
}

// the post people already like: a sentence and a row per bet. It stays put as its bets settle, and moves up once when the last one does
function HandPost({ post, ctx, lead }) {
  const { rows: all, closed, results } = post;
  const { fr, money, signed, moment, act } = ctx;
  const best = (r) => (r.status === 'won' ? profitOf(r.bet) : r.status === 'lost' ? -stakeOf(r.bet) : 0);
  const rows = closed ? [...all].sort((a, b) => best(b) - best(a)) : all;
  const isOpen = ctx.openIds.has(post.id);
  const land = moment.land.has(post.id);
  const shown = isOpen ? rows : rows.slice(0, ROWS_SHOWN);
  const total = all.reduce((sum, r) => sum + stakeOf(r.bet), 0);
  const won = all.filter((r) => r.status === 'won').length;
  const lost = all.filter((r) => r.status === 'lost').length;
  const net = netOf(all);
  const single = all.length === 1;
  const hidden = all.length - ROWS_SHOWN;
  const news = closed ? null : ctx.news[post.id];
  const record = <b>{won}-{lost}</b>;

  let sentence;
  if (results) sentence = fr ? <>a fait {record} pour <b className={net < 0 ? 'fn-lost' : 'fn-won'}>{signed(net)}</b></> : <>went {record} for <b className={net < 0 ? 'fn-lost' : 'fn-won'}>{signed(net)}</b></>;
  else if (closed) sentence = fr ? <>a fait {record} sur <b>{all.length} paris</b></> : <>went {record} on <b>{all.length} bets</b></>;
  else if (single && all[0].bet.bet_type === 'parlay') sentence = fr ? <>a placé un <b>combiné de {all[0].legs} sélections</b> pour <b>{money(total)}</b></> : <>placed a <b>{all[0].legs}-leg parlay</b> for <b>{money(total)}</b></>;
  else if (single) sentence = fr ? <>a misé <b>{money(total)}</b></> : <>bet <b>{money(total)}</b></>;
  else {
    const count = <b><Roll text={plain(all.length)} /> {fr ? 'paris' : 'bets'}</b>;
    const sum = <b><Roll text={money(total)} /></b>;
    sentence = fr ? <>a placé {count} pour {sum}</> : <>placed {count} for {sum}</>;
  }
  const closedTime = closed && !results && (fr ? `placé ${agoText(post.placedAt, fr)}, fermé ${agoText(post.at, fr)}` : `placed ${agoText(post.placedAt, fr)}, closed ${agoText(post.at, fr)}`);

  let more = fr ? 'Voir moins' : 'Show less';
  if (!isOpen) {
    more = fr ? `Voir ${hidden} de plus` : `Show ${hidden} more`;
    if (news?.won) more += fr ? `, ${news.won} vient de gagner` : `, ${news.won} just won`;
    else if (news?.lost) more += fr ? `, ${news.lost} vient de perdre` : `, ${news.lost} just lost`;
  }

  return (
    <div className={`feed-post fn-post fn-hand${land && !closed ? ' fn-land' : ''}${land && closed ? ' fn-jelly' : ''}`}>
      <div className="fn-art"><Watermark team={lead} /></div>
      <Top post={post} ctx={ctx} timeText={closedTime}>
        {sentence}
        {!closed && total >= BIG_BET && <> <span className="feed-big-mark fn-mark">{fr ? 'GROS PARI' : 'BIG BET'}</span></>}
      </Top>
      {closed && !results && <div className="fn-net"><Amount text={signed(net)} size={32} tone={net < 0 ? 'lost' : 'won'} land={land} delay={450} /></div>}
      <div className="feed-rows">
        {shown.map((r, i) => <BetRow key={r.likeKey} row={r} ctx={ctx} n={i - ROWS_SHOWN} enter={moment.land.has(`open:${post.id}`) && i >= ROWS_SHOWN} />)}
      </div>
      <WhoLit keys={all.map((r) => r.likeKey)} ctx={ctx} />
      {hidden > 0 && <button type="button" className="feed-more" onClick={() => act.toggle(post.id)}>{more}</button>}
    </div>
  );
}

// wins, Predict, Spin, a parlay that hit: same frame, only the size and the watermark change
function AmountPost({ post, ctx }) {
  const { fr, money, signed, moment } = ctx;
  const land = moment.land.has(post.id);
  const { bet, legs } = post.row || {};
  let sentence; let detail = null; let team = null;
  if (post.kind === 'win') {
    const pick = ctx.betText(bet);
    const game = ctx.gameInfo(bet);
    const side = pickedSide(game, pick);
    team = game && side ? game[side] : null;
    sentence = post.big ? (fr ? 'a frappé fort' : 'hit big') : (fr ? 'a gagné' : 'won');
    detail = <>{pick}<span>{money(stakeOf(bet))} @ {(+bet.odds).toFixed(2)}</span></>;
  } else if (post.kind === 'parlaywin') {
    sentence = fr ? <>a réussi un <b>combiné de {legs} sélections</b></> : <>hit a <b>{legs}-leg parlay</b></>;
    detail = <>{fr ? 'Sélections cachées' : 'Picks hidden'}<span>{money(stakeOf(bet))} {fr ? 'misé' : 'in'}</span></>;
  } else if (post.kind === 'predict') {
    sentence = fr ? <>a encaissé <b>Predict the Night</b></> : <>cashed <b>Predict the Night</b></>;
  } else {
    sentence = fr ? <>a réussi <b>The Spin</b></> : <>hit <b>The Spin</b></>;
  }
  return (
    <div className={`feed-post fn-post${post.big ? ' fn-big' : ''}${land ? ' fn-land' : ''}`}>
      <div className="fn-art"><Watermark team={team} kind={post.kind} /></div>
      <BetLike className="fn-solo" {...likeProps(post.likeKey, ctx)}>
        <div data-like={post.likeKey}><Top post={post} ctx={ctx}>{sentence}</Top></div>
        <Amount text={signed(post.amount)} size={post.big ? 44 : 32} tone="won" land={land} spark={post.big} slow={post.kind === 'spin'} />
        {detail && <p className="fn-detail">{detail}</p>}
        <Arrival key={ctx.arrive[post.likeKey]?.n} who={ctx.arrive[post.likeKey]} ctx={ctx} />
      </BetLike>
      <WhoLit keys={[post.likeKey]} ctx={ctx} />
    </div>
  );
}

function JackpotPost({ post, ctx }) {
  const { fr, money, moment } = ctx;
  const land = moment.land.has(post.id);
  const winners = [...post.winners].sort((a, b) => b.amount - a.amount);
  const month = new Date(`${String(post.month).slice(0, 7)}-15T12:00:00Z`).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'long', timeZone: 'UTC' });
  const places = fr ? ['1re place', '2e place', '3e place'] : ['1st place', '2nd place', '3rd place'];
  return (
    <div className={`feed-post fn-post fn-jackpot${land ? ' fn-land' : ''}`}>
      <div className="fn-art"><Watermark kind="jackpot" /></div>
      <div className="fn-jack-top"><b>{fr ? `Jackpot de ${month}` : `${month} jackpot`}</b><time>{timeAgo(post.at, fr)}</time></div>
      <div className="feed-rows">
        {winners.map((w, i) => (
          <BetLike key={w.likeKey} className={`feed-row fn-place fn-press${land ? ' fn-rise' : ''}`} style={{ '--i': winners.length - 1 - i }} {...likeProps(w.likeKey, ctx)}>
            <Face id={w.user_id} name={w.name} size={30} ctx={ctx} />
            <div className="feed-row-main" data-like={w.likeKey}><b>{ctx.nameOf(w.user_id, w.name)}</b><span>{places[i]}</span></div>
            <Amount text={money(w.amount)} size={i === 0 ? 32 : 17} tone="gold" land={land} spark={i === 0} delay={(winners.length - 1 - i) * 180 + 300} />
            <Arrival key={ctx.arrive[w.likeKey]?.n} who={ctx.arrive[w.likeKey]} ctx={ctx} />
          </BetLike>
        ))}
      </div>
    </div>
  );
}

// small results never stack up as loose lines: side by side they share one post
function LinesPost({ lines, ctx }) {
  const [open, setOpen] = useState(false);
  const { fr, money, moment } = ctx;
  const extra = lines.length - ROWS_SHOWN;
  const shown = open ? lines : lines.slice(0, ROWS_SHOWN);
  const lessOrMore = open ? (fr ? 'Voir moins' : 'Show less') : (fr ? `et ${extra} de plus` : `and ${extra} more small result${extra > 1 ? 's' : ''}`);
  return (
    <div className="feed-post fn-post fn-lines">
      {shown.map((l) => (
        <BetLike key={l.id} className={`fn-line fn-press${moment.land.has(l.id) ? ' fn-land' : ''}`} {...likeProps(l.likeKey, ctx)}>
          <Face id={l.user_id} size={28} ctx={ctx} />
          <p data-like={l.likeKey}><b>{ctx.nameOf(l.user_id)}</b> {ctx.lineText(l)} <time>{timeAgo(l.at, fr)}</time></p>
          <b className={`fn-line-amt ${l.tone}`}>{l.tone === 'lost' ? '-' : '+'}{money(l.amount)}</b>
          <Arrival key={ctx.arrive[l.likeKey]?.n} who={ctx.arrive[l.likeKey]} ctx={ctx} />
        </BetLike>
      ))}
      {extra > 0 && <button type="button" className="feed-more" onClick={() => setOpen((o) => !o)}>{lessOrMore}</button>}
    </div>
  );
}

// the Feed: one column of posts built from the items. The page sits still, and only a friend or a game makes something move
export default function Feed({ items, loaded, failed, betsCap = Infinity, likes, onLike, profile, players, avatarOf, gameInfo, liveOf, lang, money, betText }) {
  const fr = lang === 'fr';
  const me = profile?.id;
  const peopleById = useMemo(() => Object.fromEntries((players || []).map((p) => [p.id, p])), [players]);
  const nameOf = (id, fallback) => peopleById[id]?.username || fallback || demoName(id) || (fr ? 'Joueur' : 'Player');
  const signed = (n) => `${n < 0 ? '-' : '+'}${money(Math.abs(n))}`;
  const pickName = (row) => (row.bet.bet_type === 'parlay' ? (fr ? `combiné de ${row.legs} sélections` : `${row.legs}-leg parlay`) : betText(row.bet));
  const lineText = (l) => {
    if (l.tone === 'lost') return fr ? `a mangé une perte sur ${pickName(l.row)}` : `ate a loss on ${pickName(l.row)}`;
    return fr ? `a encaissé ${pickName(l.row)}` : `cashed ${pickName(l.row)}`;
  };

  const target = useMemo(() => buildPosts(items, betsCap), [items, betsCap]);
  const boost = items.some((i) => i.kind === 'boost');

  const [shown, setShown] = useState(null);
  const [moment, setMoment] = useState(NO_MOMENT);
  const [arrive, setArrive] = useState({});
  const [news, setNews] = useState({});
  const [openIds, setOpenIds] = useState(() => new Set());
  const [heldN, setHeldN] = useState(0);
  const [missed, setMissed] = useState([]);
  const [tray, setTray] = useState(false);
  const [first, setFirst] = useState(true);

  const shownNow = useRef(null);
  const targetNow = useRef(target);
  targetNow.current = target;
  const heldNow = useRef(0);
  const openNow = useRef(openIds);
  openNow.current = openIds;
  const director = useRef({ dirty: false, busy: false, flames: [] });
  const knownLikes = useRef(new Map());
  const unseenFlames = useRef(new Map());
  const lastVisit = useRef(null);
  const replays = useRef({ at: 0, n: 0 });
  const timers = useRef(new Set());
  const engine = useRef(null);
  const leads = useRef(new Map());
  const listRef = useRef(null);
  const tops = useRef(new Map());
  const loops = useRef(null);
  const nextN = useRef(0);

  const later = (fn, ms) => { const t = setTimeout(() => { timers.current.delete(t); fn(); }, ms); timers.current.add(t); };
  const addMissed = (m) => setMissed((list) => [{ id: `m${nextN.current++}`, ...m }, ...list].slice(0, 6));
  const commit = (list) => { shownNow.current = list; setShown(list); };

  const describe = (p) => {
    if (p.kind === 'hand') {
      const won = p.rows.filter((r) => r.status === 'won').length;
      const lost = p.rows.filter((r) => r.status === 'lost').length;
      return { text: fr ? `a fait ${won}-${lost}` : `went ${won}-${lost}`, amount: netOf(p.rows) };
    }
    if (p.kind === 'line') return { text: lineText(p), amount: p.tone === 'lost' ? -p.amount : p.amount };
    if (p.kind === 'predict') return { text: 'Predict the Night', amount: p.amount };
    if (p.kind === 'spin') return { text: 'The Spin', amount: p.amount };
    return { text: pickName(p.row), amount: p.amount };
  };

  // brings the page up to the newest items. Everything that changed since the last time plays as one wave
  const applySnapshot = () => {
    const prev = shownNow.current;
    const next = targetNow.current;
    const prevById = new Map(prev.map((p) => [p.id, p]));
    const nextById = new Map(next.map((p) => [p.id, p]));
    const prevRows = new Map(prev.flatMap((p) => (p.rows ? p.rows.map((r) => [r.likeKey, r]) : [])));
    const withNewRows = (q, p) => {
      const rows = new Map(p.rows.map((r) => [r.likeKey, r]));
      return { ...q, rows: q.rows.map((r) => rows.get(r.likeKey) || r) };
    };
    const changed = next.filter((p) => { const q = prevById.get(p.id); return !q || shapeOf(q) !== shapeOf(p); });
    // the last row flips where it is first, and the post moves up a moment later
    const closing = next.filter((p) => {
      const q = prevById.get(p.id);
      return p.closed && q?.kind === 'hand' && !q.closed && q.rows.some((r) => r.status === 'open');
    });
    const closingIds = new Set(closing.map((p) => p.id));

    let list;
    let landing;
    if (scrollTop() > TOP_ZONE && (changed.length || prev.some((q) => !nextById.has(q.id)))) {
      // you're reading further down: rows can change in place, anything that would move posts waits behind the pill
      list = prev.map((q) => {
        const p = nextById.get(q.id);
        if (!p) return q;
        if (shapeOf(q) === shapeOf(p)) return { ...p, at: q.at };
        return q.rows && p.rows && q.rows.length === p.rows.length ? withNewRows(q, p) : q;
      });
      landing = [];
      heldNow.current = changed.length;
      setHeldN(changed.length);
    } else {
      list = next.map((p) => (closingIds.has(p.id) ? withNewRows(prevById.get(p.id), p) : p)).sort(byNewest);
      landing = changed.filter((p) => !closingIds.has(p.id));
      heldNow.current = 0;
      setHeldN(0);
      if (closing.length) later(() => { director.current.dirty = true; engine.current.runNext(); }, 900);
    }

    const flip = new Set();
    const rowNew = new Set();
    const bump = [];
    const newsNext = {};
    list.forEach((p) => {
      if (!p.rows) return;
      const q = prevById.get(p.id);
      p.rows.forEach((r, i) => {
        const before = prevRows.get(r.likeKey);
        if (q?.rows && !before) rowNew.add(r.likeKey);
        if (!before || before.status !== 'open' || r.status === 'open' || r.status === 'void') return;
        if (onScreen(`[data-like="${r.likeKey}"]`)) { flip.add(r.likeKey); bump.push(r.likeKey); return; }
        if (closingIds.has(p.id)) return;   // the post moving up says it all
        if (!p.closed && !openNow.current.has(p.id) && i >= ROWS_SHOWN) {
          const n = newsNext[p.id] || { won: 0, lost: 0 };
          newsNext[p.id] = { ...n, [r.status]: n[r.status] + 1 };
          return;
        }
        const amount = r.status === 'won' ? profitOf(r.bet) : -stakeOf(r.bet);
        addMissed({ postId: p.id, user_id: r.user_id, text: `${r.status === 'won' ? (fr ? 'a encaissé' : 'cashed') : (fr ? 'a perdu sur' : 'lost on')} ${pickName(r)}`, amount });
      });
    });

    commit(list);
    if (Object.keys(newsNext).length) {
      setNews((all) => {
        const merged = { ...all };
        Object.entries(newsNext).forEach(([id, n]) => { const o = merged[id] || { won: 0, lost: 0 }; merged[id] = { won: o.won + n.won, lost: o.lost + n.lost }; });
        return merged;
      });
    }
    if (!landing.length && !flip.size && !rowNew.size) return null;
    setMoment({ land: new Set(landing.length <= LANDS_MAX ? landing.map((p) => p.id) : []), flip, rowNew });
    later(() => setMoment(NO_MOMENT), 1800);
    later(() => bump.forEach((key) => { const el = document.querySelector(`[data-like="${key}"]`); animateEl(el && el.closest('.feed-row'), ROW_BUMP, 240); }), 30);
    return landing.some((p) => p.closed || p.big || p.kind === 'jackpot' || p.kind === 'spin') ? 1200 : 600;
  };

  const flameArrive = (key, user_id) => {
    const n = nextN.current++;
    setArrive((all) => ({ ...all, [key]: { user_id, n } }));
    later(() => setArrive((all) => { if (all[key]?.n !== n) return all; const { [key]: done, ...rest } = all; return rest; }), 1900);
  };

  // one moment at a time, with quiet in between
  const runNext = () => {
    const d = director.current;
    if (d.busy || !shownNow.current) return;
    let hold = null;
    if (d.dirty) {
      d.dirty = false;
      hold = applySnapshot();
    } else if (d.flames.length) {
      const f = d.flames.shift();
      flameArrive(f.key, f.user_id);
      hold = 300;
    } else return;
    if (hold === null) { runNext(); return; }
    d.busy = true;
    later(() => { d.busy = false; engine.current.runNext(); }, hold + GAP_MS);
  };

  // flames you missed play as they come on screen: at most 3 at a time, once each
  const seenPost = (id) => {
    const post = view.find((p) => p.id === id);
    if (!post) return;
    keysOf(post).filter((k) => unseenFlames.current.has(k)).forEach((k) => {
      const user_id = unseenFlames.current.get(k);
      unseenFlames.current.delete(k);
      const r = replays.current;
      if (Date.now() - r.at > 2000) { r.at = Date.now(); r.n = 0; }
      const n = r.n++;
      if (n < 3) later(() => engine.current.flameArrive(k, user_id), 700 + n * 180);
    });
  };

  // the first load shows everything still. After that, each new snapshot is a change to play
  useEffect(() => {
    if (!loaded) return;
    if (!shownNow.current) {
      commit(target);
      let last = null;
      try { last = +localStorage.getItem(LAST_VISIT) || null; localStorage.setItem(LAST_VISIT, String(Date.now())); } catch { /* private window */ }
      lastVisit.current = last;
      if (last) {
        const away = target.filter((p) => time(p.at) > last && p.user_id && p.user_id !== me && (p.kind !== 'hand' || p.closed)).slice(0, 6);
        setMissed(away.map((p) => ({ id: `m${nextN.current++}`, postId: p.id, user_id: p.user_id, ...describe(p) })));
      }
      later(() => setFirst(false), 900);
      return;
    }
    director.current.dirty = true;
    runNext();
  }, [loaded, target]);

  // a friend's flame: from the likes as they change
  useEffect(() => {
    const known = knownLikes.current;
    const d = director.current;
    const now = Date.now();
    Object.entries(likes).forEach(([key, list]) => {
      const had = known.get(key);
      known.set(key, new Set(list.map((l) => l.user_id)));
      list.forEach((l) => {
        if (l.user_id === me || (had && had.has(l.user_id))) return;
        const at = l.created_at ? time(l.created_at) : 0;
        // a key seen for the first time can be the old likes loading, or the very first flame on that bet
        if (!had && now - at >= FRESH_LIKE_MS) {
          if (lastVisit.current && at > lastVisit.current) unseenFlames.current.set(key, l.user_id);
          return;
        }
        if (shownNow.current && onScreen(`[data-like="${key}"]`) && d.flames.length < 3) d.flames.push({ key, user_id: l.user_id });
        else unseenFlames.current.set(key, l.user_id);
      });
    });
    if (d.flames.length) runNext();
  }, [likes, me]);

  useEffect(() => {
    const all = timers.current;
    return () => { all.forEach(clearTimeout); all.clear(); };
  }, []);

  // posts held back land as soon as you're back at the top
  useEffect(() => {
    const onScroll = () => {
      if (scrollTop() > TOP_ZONE || !heldNow.current) return;
      director.current.dirty = true;
      engine.current.runNext();
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  // one observer: what's off screen never loops, and only a few things on screen do
  useEffect(() => {
    const visible = new Set();
    const budget = () => {
      document.querySelectorAll('[data-flick]').forEach((el) => el.removeAttribute('data-flick'));
      const order = [...visible].filter((el) => el.isConnected).sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      ['.bet-like.hot', '.fn-dot'].forEach((sel) => {
        order.flatMap((el) => [...el.querySelectorAll(sel)]).slice(0, LOOP_BUDGET).forEach((el) => el.setAttribute('data-flick', ''));
      });
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { visible.add(e.target); engine.current.seenPost(e.target.dataset.post); } else visible.delete(e.target);
      });
      budget();
    }, { threshold: 0.1 });
    loops.current = { io, budget };
    return () => { io.disconnect(); loops.current = null; };
  }, []);
  useEffect(() => {
    if (!loops.current) return;
    document.querySelectorAll('[data-post]').forEach((el) => loops.current.io.observe(el));
    loops.current.budget();
  });

  const view = useMemo(() => {
    const out = [];
    (shown || []).forEach((p) => {
      const last = out[out.length - 1];
      if (p.kind === 'line' && last?.kind === 'lines') last.lines.push(p);
      else if (p.kind === 'line') out.push({ kind: 'lines', lines: [p] });
      else out.push(p);
    });
    out.forEach((p) => { if (p.kind === 'lines') p.id = `lines:${p.lines[p.lines.length - 1].id}`; });
    return out;
  }, [shown]);

  engine.current = { runNext, flameArrive, seenPost };

  // the watermark is the biggest or best bet, unless the last two posts just showed that logo. Once chosen it stays until the post closes
  const recent = [];
  view.forEach((p) => {
    if (p.kind !== 'hand') return;
    const teamOf = (r) => {
      if (r.bet.bet_type === 'parlay') return null;
      const game = gameInfo(r.bet);
      const side = pickedSide(game, betText(r.bet));
      return game && side ? game[side] : null;
    };
    const score = (r) => (p.closed ? (r.status === 'won' ? profitOf(r.bet) : r.status === 'lost' ? -stakeOf(r.bet) : 0) : stakeOf(r.bet));
    const saved = leads.current.get(p.id);
    let lead = saved && saved.closed === !!p.closed ? saved.team : null;
    if (!lead) {
      const teams = [...p.rows].sort((a, b) => score(b) - score(a)).map(teamOf).filter(Boolean);
      lead = teams.find((t) => !recent.includes(t.abbrev)) || teams[0] || null;
    }
    if (!lead) return;
    leads.current.set(p.id, { team: lead, closed: !!p.closed });
    recent.push(lead.abbrev);
    if (recent.length > 2) recent.shift();
  });

  // posts glide to their new place with a transform, whatever moved them
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const next = new Map();
    const sc = scrollTop();
    list.querySelectorAll(':scope > [data-post]').forEach((el) => {
      const id = el.dataset.post;
      const top = el.offsetTop;
      const prev = tops.current.get(id);
      next.set(id, { top, sc });
      if (!prev || REDUCED) return;
      const d = (prev.top - prev.sc) - (top - sc);
      const screenTop = el.getBoundingClientRect().top;
      if (Math.abs(d) < 2 || Math.abs(d) > window.innerHeight * 1.5 || screenTop > window.innerHeight || screenTop + el.offsetHeight < 0) return;
      const far = Math.abs(d) > 200;
      if (far) el.style.zIndex = 2;
      const anim = el.animate([{ transform: `translateY(${d}px)` }, { transform: 'none' }], { duration: far ? 450 : 320, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
      anim.onfinish = () => { el.style.zIndex = ''; };
    });
    tops.current = next;
  }, [view]);

  const toggle = (id) => {
    const opening = !openIds.has(id);
    setOpenIds((all) => { const n = new Set(all); if (opening) n.add(id); else n.delete(id); return n; });
    setNews(({ [id]: gone, ...rest }) => rest);
    if (!opening) return;
    setMoment((m) => ({ ...m, land: new Set([...m.land, `open:${id}`]) }));
    later(() => setMoment((m) => { const land = new Set(m.land); land.delete(`open:${id}`); return { ...m, land }; }), 900);
  };

  const ctx = {
    fr, money, signed, betText, likes, onLike, me, avatarOf, gameInfo, liveOf, peopleById, nameOf, moment, arrive, news, openIds, lineText,
    act: { toggle },
    parlayName: (legs) => (fr ? `Combiné de ${legs} sélections` : `${legs}-leg parlay`),
  };

  const liveRows = (shown || []).flatMap((p) => (p.kind === 'hand' && !p.closed ? p.rows.filter((r) => r.status === 'open' && r.bet.bet_type !== 'parlay' && liveOf?.(r.bet)).map(() => p.id) : []));
  const viewIdOf = (id) => view.find((p) => p.id === id || p.lines?.some((l) => l.id === id))?.id || null;
  const goTo = (id) => {
    const el = document.querySelector(`[data-post="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
    later(() => animateEl(el.firstElementChild, JELLY, 550), 450);
  };
  const closeTray = () => { setTray(false); setMissed([]); };

  return (
    <div className="fn-page">
      <div className={`fn-pills${heldN > 0 ? ' mid' : ''}`}>
        {heldN > 0 && <button type="button" className="fn-pill" onClick={goTop}>{fr ? `${heldN} nouveau${heldN > 1 ? 'x' : ''}` : `${heldN} new`}</button>}
        {heldN === 0 && missed.length >= 2 && !tray && <button type="button" className="fn-pill" onClick={() => setTray(true)}>{fr ? `${missed.length} pendant ton absence` : `${missed.length} while you were away`}</button>}
        {tray && (
          <div className="fn-tray">
            {missed.filter((m) => viewIdOf(m.postId)).map((m, i) => (
              <button type="button" key={m.id} className="fn-tray-row" style={{ '--i': i }} onClick={() => { const id = viewIdOf(m.postId); closeTray(); if (id) goTo(id); }}>
                <Face id={m.user_id} size={26} ctx={ctx} />
                <span><b>{nameOf(m.user_id)}</b> {m.text}</span>
                <b className={`fn-line-amt ${m.amount < 0 ? 'lost' : 'won'}`}>{signed(m.amount)}</b>
              </button>
            ))}
            <button type="button" className="feed-more" onClick={closeTray}>{fr ? 'Compris' : 'Got it'}</button>
          </div>
        )}
      </div>

      <div className="feed-header">
        <h2>Feed</h2>
        {liveRows.length > 0 && <button type="button" className="feed-live fn-livepill" onClick={() => goTo(liveRows[0])}><i />{liveRows.length} {fr ? 'en direct' : 'live'}</button>}
      </div>
      <div className="feed-list fn-list" ref={listRef}>
        {boost && <p className="feed-boost"><b>Boost Weekend</b> {fr ? 'Cotes ×2 jusqu’à dimanche minuit' : 'Odds ×2 until Sunday at midnight'}</p>}
        {failed && <p className="feed-empty">{fr ? 'Le fil ne peut pas se charger.' : 'The feed could not load.'}</p>}
        {!failed && loaded && shown && view.length === 0 && <p className="feed-empty">{fr ? 'Rien pour le moment.' : 'Nothing yet.'}</p>}
        {view.map((p, i) => (
          <React.Fragment key={p.id}>
          {(i === 0 || dayOf(view[i - 1]) !== dayOf(p)) && <h3 className="fn-day">{dayLabel(dayOf(p), fr)}</h3>}
          <div data-post={p.id} className={`fn-item${first ? ' feed-item' : ''}`} style={{ '--i': Math.min(i, 5) }}>
            {p.kind === 'hand' && <HandPost post={p} ctx={ctx} lead={leads.current.get(p.id)?.team} />}
            {['win', 'parlaywin', 'predict', 'spin'].includes(p.kind) && <AmountPost post={p} ctx={ctx} />}
            {p.kind === 'jackpot' && <JackpotPost post={p} ctx={ctx} />}
            {p.kind === 'lines' && <LinesPost lines={p.lines} ctx={ctx} />}
          </div>
          </React.Fragment>
        ))}
        {view.length >= POSTS_MAX && <p className="fn-end">{fr ? `Ce sont les ${POSTS_MAX} derniers` : `That’s the last ${POSTS_MAX}`}</p>}
      </div>
    </div>
  );
}
